import React, { useState, useMemo } from 'react';
import { Calendar, Send, AlertCircle, User, Briefcase } from 'lucide-react';
import { Button } from '../../../components/common/Button';
import type { UserProfile, BankHoliday } from '../../../utils/types';

interface AdminHolidayRequestFormProps {
  users: UserProfile[];
  onSubmit: (data: {
    user_id: number;
    start_date: string;
    end_date: string;
    reason: string;
    is_half_day: boolean;
  }) => void;
  isSubmitting?: boolean;
  bankHolidays?: BankHoliday[] | any;
}

export const AdminHolidayRequestForm: React.FC<AdminHolidayRequestFormProps> = ({
  users,
  onSubmit,
  isSubmitting,
  bankHolidays = [],
}) => {
  const [formData, setFormData] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    is_half_day: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create a Set of bank holiday dates for O(1) lookup
  // Handle both array response and paginated response
  const holidaysList = Array.isArray(bankHolidays) 
    ? bankHolidays 
    : (bankHolidays as any)?.results || [];
  
  const bankHolidayDates = useMemo(() => {
    return new Set(holidaysList.map((bh: BankHoliday) => bh.date));
  }, [holidaysList]);

  // Sort users by name
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = `${a.user_details?.first_name || ''} ${a.user_details?.last_name || ''}`.trim();
      const nameB = `${b.user_details?.first_name || ''} ${b.user_details?.last_name || ''}`.trim();
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.user_id) {
      newErrors.user_id = 'Please select a user';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (formData.start_date && formData.end_date) {
      const start = new Date(formData.start_date);
      const end = new Date(formData.end_date);

      if (end < start) {
        newErrors.end_date = 'End date must be after or equal to start date';
      }

      // Check if dates span multiple years
      if (start.getFullYear() !== end.getFullYear()) {
        newErrors.end_date = 'Holiday cannot span multiple years';
      }
    }

    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        user_id: parseInt(formData.user_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason,
        is_half_day: formData.is_half_day,
      });
      // Reset form
      setFormData({
        user_id: '',
        start_date: '',
        end_date: '',
        reason: '',
        is_half_day: false,
      });
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Calculate actual working days (excludes weekends and bank holidays)
  const calculateWorkingDays = (): { total: number; working: number; excluded: number } => {
    if (!formData.start_date || !formData.end_date) {
      return { total: 0, working: 0, excluded: 0 };
    }

    if (formData.is_half_day) {
      return { total: 0.5, working: 0.5, excluded: 0 };
    }

    const start = new Date(formData.start_date);
    const end = new Date(formData.end_date);
    
    let totalDays = 0;
    let workingDays = 0;
    let excludedDays = 0;
    
    const current = new Date(start);
    
    while (current <= end) {
      totalDays++;
      const dateString = current.toISOString().split('T')[0];
      const dayOfWeek = current.getDay();
      
      // Check if it's a weekend (0 = Sunday, 6 = Saturday)
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // Check if it's a bank holiday
      const isBankHoliday = bankHolidayDates.has(dateString);
      
      if (isWeekend || isBankHoliday) {
        excludedDays++;
      } else {
        workingDays++;
      }
      
      current.setDate(current.getDate() + 1);
    }
    
    return { total: totalDays, working: workingDays, excluded: excludedDays };
  };

  const { total, working, excluded } = calculateWorkingDays();

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <User className="w-5 h-5 text-blue-500" />
        Create Holiday Request for User
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* User Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select User <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.user_id}
            onChange={(e) => handleChange('user_id', e.target.value)}
            className={`form-input w-full ${errors.user_id ? 'border-red-500' : ''}`}
            disabled={isSubmitting}
          >
            <option value="">-- Select a user --</option>
            {sortedUsers.map((user) => (
              <option key={user.id} value={user.user}>
                {user.user_details?.first_name} {user.user_details?.last_name} 
                ({user.user_details?.username}) - Allocated: {user.allocated_holidays || 0} days
              </option>
            ))}
          </select>
          {errors.user_id && (
            <p className="text-red-500 text-sm mt-1">{errors.user_id}</p>
          )}
        </div>

        {/* Start Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.start_date}
            onChange={(e) => handleChange('start_date', e.target.value)}
            className={`form-input w-full ${errors.start_date ? 'border-red-500' : ''}`}
            disabled={isSubmitting}
          />
          {errors.start_date && (
            <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>
          )}
        </div>

        {/* End Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            End Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.end_date}
            onChange={(e) => handleChange('end_date', e.target.value)}
            className={`form-input w-full ${errors.end_date ? 'border-red-500' : ''}`}
            min={formData.start_date}
            disabled={isSubmitting}
          />
          {errors.end_date && (
            <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>
          )}
        </div>

        {/* Reason */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            className={`form-input w-full h-20 resize-none ${errors.reason ? 'border-red-500' : ''}`}
            placeholder="Enter reason for the holiday request..."
            disabled={isSubmitting}
          />
          {errors.reason && (
            <p className="text-red-500 text-sm mt-1">{errors.reason}</p>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_half_day}
            onChange={(e) => handleChange('is_half_day', e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300"
            disabled={isSubmitting}
          />
          <span className="text-sm text-gray-700">Half Day (0.5 days)</span>
        </label>

        {formData.start_date && formData.end_date && (
          <div className="text-right text-gray-600">
            {formData.is_half_day ? (
              <span className="text-sm font-medium">Half Day: 0.5 days</span>
            ) : (
              <div className="text-sm">
                <div className="flex items-center gap-2 justify-end">
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  <span className="font-semibold">
                    Working Days: {working} day{working !== 1 ? 's' : ''}
                  </span>
                </div>
                {excluded > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    ({total} days selected - {excluded} weekend/bank holiday{excluded !== 1 ? 's' : ''} excluded)
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="submit"
          icon={<Send className="w-4 h-4" />}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          Create Request
        </Button>
      </div>
    </form>
  );
};

export default AdminHolidayRequestForm;
